import { useEffect, useState } from "react";
import { overallHeatMap } from "../../../../services/api-services";
import { toPng } from "html-to-image";
import Remark from "../Remark";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const Dimension = ({ selectedCompanyId, graphName, myhandleExport }) => {
  const [heatmapData, setHeatMapData] = useState([]);
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState(null);
  const [isAttempt, setIsAttempt] = useState("");
  const [colorRanges, setColorRanges] = useState([]);

  const getHeatmapData = async () => {
    try {
      const response = await overallHeatMap(selectedCompanyId, graphName);
      if (response?.data?.responseCode === 200) {
        const { remark, remarkId, isAttempt, responseBody, companyColorList } = response.data;
        setRemark(remark || "");
        setRemarkId(remarkId || null);
        setIsAttempt(isAttempt ?? 0);
        setHeatMapData(responseBody || []);
        setColorRanges(companyColorList || []);

        generateHtmlImage("overall_heatmap", remark);
      }
    } catch (err) {
      console.error("Error fetching heatmap data:", err);
    }
  };

  useEffect(() => {
    getHeatmapData();
  }, [selectedCompanyId]);

  // Function to get the color for a percentage dynamically
  const getColorForPercentage = (percentage) => {
    if (!colorRanges.length) return "#000000"; // Default color if no range is available

    for (const range of colorRanges) {
      if (percentage >= range.rangeFrom && percentage < range.rangeTo) {
        return range.colorName;
      }
    }
    return "#000000"; // Default fallback color
  };

  const generateHtmlImage = async (elementId, remarkValue) => {
    const node = document.getElementById(elementId);
    try {
      const dataUrl = await toPng(node);
      const chartData = JSON.parse(localStorage.getItem("chartImageData")) || [];
      const updatedChartData = chartData.map((item) =>
        item.id === "overall_heatmap" ? { ...item, image: dataUrl, remark: remarkValue } : item
      );
      localStorage.setItem("chartImageData", JSON.stringify(updatedChartData));
    } catch (error) {
      console.error("Error generating image from HTML:", error);
    }
  };

  const exportSummaryToExcel = (data, fileName = "Overall Heatmap") => {
    const title = [["Overall Heatmap"]]; // Title row
    const headers = ["Dimension/Sub-Dimension Name", "Count"];
    const rows = [];
    data?.forEach(dimension => {
      // Push the main dimension
      rows.push([
        dimension?.dimensionName,
        dimension?.percentage?.toFixed(2),
      ]);

      // Push its subdimensions
      dimension?.subDimensionOverallHeatmapChartResponseList?.forEach(sub => {
        rows.push([
          `  - ${sub?.subDimensionName}`,
          sub?.percentage?.toFixed(2),
        ]);
      });
    });

    const sheetData = [...title, [], headers, ...rows]; // Empty row between title and headers
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    // Merge cells to span the title across two columns
    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 }, // start cell (row 0, column 0)
        e: { r: 0, c: 1 }, // end cell (row 0, column 1)
      },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, fileName);
  };

  return (
    <>
      <div className="text-end">
        <button className="mb-3 px-3 py-2 text-white bg-primary rounded border-0 cursor-pointer me-3" onClick={() => exportSummaryToExcel(heatmapData)}>Download Excel</button>
      </div>

      <div>
        <div className="container-fluid chart5" data-heading="Overall Heatmap" data-remark={remark}
          id="overall_heatmap">
          {/* Legend */}
          <div className="legend-row">
            {colorRanges.map((item, index) => (
              <div key={index} className="legend-item">
                <div className="legend-color" style={{ backgroundColor: item.colorName }}></div>
                <span className="legend-label">{`${item.rangeFrom}% - ${item.rangeTo}%`}</span>
              </div>
            ))}
          </div>

          {/* Header Row */}
          <div className="header-row">
            {heatmapData?.map((item, index) => (
              <>

                <div
                  key={index}
                  className="header-cell"
                  style={{
                    backgroundColor: getColorForPercentage(item.percentage),
                  }}
                >
                  <p>{item.dimensionName}</p>
                  <p>{item.percentage?.toFixed(2)}%</p>
                </div>
              </>

            ))}
          </div>

          {/* Data Rows - Showing Subdimension Percentages */}
          <div className="data-row mb-2">
            {heatmapData?.map((item) => (
              <div data-heading="Overall Heatmap" data-remark={remark}>
                <div key={item.id} className="column">
                  {item.subDimensionOverallHeatmapChartResponseList?.map((subDimension) => (
                    <div
                      key={subDimension.id}
                      className="data-cell"
                      style={{
                        backgroundColor: getColorForPercentage(subDimension?.percentage),
                      }}
                    >
                      <p>{subDimension?.subDimensionName}</p>
                      <p>{subDimension?.percentage?.toFixed(2)}%</p> {/* FIX: Display subdimension percentage */}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <Remark
          graphName="OverallHeatmap"
          selectedCompanyId={selectedCompanyId}
          initialRemark={remark}
          remarkId={remarkId}
          isAttempt={isAttempt}
          setRemarkprops={setRemark}
        />
      </div>
    </>

  );
};

export default Dimension;
